/**
 * TicketThermalViewControls — Toggle reutilizable Web vs Térmica + ancho 80/58mm
 * + formato de ticket (Completo/Compacto) cuando el caller lo provee.
 *
 * El selector de formato es OPCIONAL (props `ticketFormato` + `onTicketFormatoChange`):
 * solo los tickets de venta lo muestran; cierre/jornada/movimiento etc. no cambian.
 */

import type { TicketViewMode } from "../hooks/useTicketThermalViewState";
import type { TicketFormato } from "../utils/businessConfigStorage";

interface TicketThermalViewControlsProps {
  viewMode: TicketViewMode;
  onViewModeChange: (mode: TicketViewMode) => void;
  paperWidthMm: 58 | 80;
  onPaperWidthChange: (mm: 58 | 80) => void;
  /** Formato térmico activo (opcional; solo se renderiza si se provee). */
  ticketFormato?: TicketFormato;
  /** Cambio de formato elegido por el negocio (opcional). */
  onTicketFormatoChange?: (formato: TicketFormato) => void;
  /**
   * Cuando el comprobante NO permite Compacto/Ultra (puntos, crédito,
   * devoluciones, anulaciones, saldo o copia interna), se deshabilitan y se
   * muestra el motivo. (Opcional; los tickets no-venta lo omiten.)
   */
  compactDisabledReason?: string;
}

export function TicketThermalViewControls({
  viewMode,
  onViewModeChange,
  paperWidthMm,
  onPaperWidthChange,
  ticketFormato,
  onTicketFormatoChange,
  compactDisabledReason,
}: TicketThermalViewControlsProps) {
  const cols = paperWidthMm === 80 ? 48 : 32;

  return (
    <div className="no-print shrink-0 px-2 sm:px-4 py-2 bg-gray-100 dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700 flex flex-wrap items-center justify-center gap-2">
      <div className="inline-flex rounded-lg overflow-hidden border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900">
        <button
          type="button"
          onClick={() => onViewModeChange("web")}
          className={`px-3 py-1.5 text-[10px] sm:text-xs font-semibold transition-colors ${
            viewMode === "web"
              ? "bg-primary-600 text-white"
              : "text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-800"
          }`}
        >
          Vista Web
        </button>
        <button
          type="button"
          onClick={() => onViewModeChange("thermal")}
          className={`px-3 py-1.5 text-[10px] sm:text-xs font-semibold transition-colors ${
            viewMode === "thermal"
              ? "bg-primary-600 text-white"
              : "text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-800"
          }`}
        >
          Vista Térmica Real
        </button>
      </div>
      {viewMode === "thermal" && (
        <div className="inline-flex rounded-lg overflow-hidden border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900">
          <button
            type="button"
            onClick={() => onPaperWidthChange(80)}
            className={`px-2.5 py-1.5 text-[10px] sm:text-xs font-medium transition-colors ${
              paperWidthMm === 80
                ? "bg-gray-800 text-white"
                : "text-gray-600 dark:text-slate-300"
            }`}
          >
            80mm
          </button>
          <button
            type="button"
            onClick={() => onPaperWidthChange(58)}
            className={`px-2.5 py-1.5 text-[10px] sm:text-xs font-medium transition-colors ${
              paperWidthMm === 58
                ? "bg-gray-800 text-white"
                : "text-gray-600 dark:text-slate-300"
            }`}
          >
            58mm
          </button>
        </div>
      )}
      {viewMode === "thermal" && ticketFormato && onTicketFormatoChange && (
        <div className="inline-flex rounded-lg overflow-hidden border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900">
          <button
            type="button"
            onClick={() => onTicketFormatoChange("completo")}
            title="Formato actual con rejillas y detalle extenso"
            className={`px-2.5 py-1.5 text-[10px] sm:text-xs font-medium transition-colors ${
              ticketFormato === "completo"
                ? "bg-gray-800 text-white"
                : "text-gray-600 dark:text-slate-300"
            }`}
          >
            Completo
          </button>
          <button
            type="button"
            onClick={() => onTicketFormatoChange("compacto")}
            title={
              compactDisabledReason
                ? `${compactDisabledReason} (puedes ver la vista previa; la impresión real será Completo)`
                : "Formato resumido que ahorra papel (una línea por producto)"
            }
            className={`px-2.5 py-1.5 text-[10px] sm:text-xs font-medium transition-colors ${
              ticketFormato === "compacto"
                ? "bg-gray-800 text-white"
                : "text-gray-600 dark:text-slate-300"
            }`}
          >
            Compacto
          </button>
          <button
            type="button"
            onClick={() => onTicketFormatoChange("ultracompacto")}
            title={
              compactDisabledReason
                ? `${compactDisabledReason} (puedes ver la vista previa; la impresión real será Completo)`
                : "Formato ultracompacto: columnas sin rejillas (máximo ahorro)"
            }
            className={`px-2.5 py-1.5 text-[10px] sm:text-xs font-medium transition-colors ${
              ticketFormato === "ultracompacto"
                ? "bg-gray-800 text-white"
                : "text-gray-600 dark:text-slate-300"
            }`}
          >
            Ultra
          </button>
        </div>
      )}
      {viewMode === "thermal" && compactDisabledReason && (
        <span className="text-[10px] text-amber-700 dark:text-amber-400 w-full text-center sm:w-auto">
          {compactDisabledReason} La impresión real será Completo; aquí puedes
          ver cómo se vería.
        </span>
      )}
      {viewMode === "thermal" && (
        <span className="text-[10px] text-gray-500 w-full text-center sm:w-auto">
          {paperWidthMm}mm · {cols} columnas
        </span>
      )}
    </div>
  );
}

/** Modal más estrecho en preview (venta / cierre / movimiento / credito). */
export const TICKET_PREVIEW_MODAL_WIDTH_CLASS = "max-w-sm";
